// AdditionalServicesSection.js
import React from 'react';
import { ArrowRight } from 'lucide-react';
import hospiceCareImage from '../assets/hospice-care-showcase.jpg';
import specialtyCareImage from '../assets/specialty-care-showcase.jpg';

const AdditionalServicesSection = () => {
  return (
    <section className="bg-cream py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-20">

        {/* Hospice Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Image */}
          <div>
            <img
              src={hospiceCareImage}
              alt="A carer comforting an older woman at home"
              className="w-full h-[420px] lg:h-[520px] object-cover rounded-2xl"
            />
          </div>

          {/* Right Content */}
          <div>
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Hospice Care
            </p>
            <h3 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-6">
              Comfort-focused support for life's final chapter
            </h3>

            <div className="space-y-4 text-muted text-base leading-relaxed mb-8">
              <p>
                Hospice care provides compassionate support for seniors with life-limiting conditions and their families. The carers we refer focus on comfort, dignity, and quality of life, assisting with daily needs while providing emotional support. This approach allows seniors to spend their final days in the familiarity of home, surrounded by care and compassion.
              </p>
              <p>
                Families benefit from reassurance and guidance during this sensitive time. By supporting both seniors and their loved ones, our carers help reduce stress, manage symptoms, and ensure that every day is as comfortable and meaningful as possible. Our hospice care involves:
              </p>
            </div>

            <ul className="space-y-3">
              {['Comfort-focused care', 'Emotional support', 'Daily living assistance'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary">
                    <ArrowRight size={13} />
                  </span>
                  <span className="text-ink font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Specialty Care */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left Content */}
          <div className="lg:order-1 order-2">
            <p className="text-primary text-xs md:text-sm font-medium tracking-[0.22em] uppercase mb-5">
              Specialty Care
            </p>
            <h3 className="font-display font-light text-ink leading-[1.1] text-3xl md:text-4xl mb-6">
              Focused support for complex and ongoing needs
            </h3>

            <div className="space-y-4 text-muted text-base leading-relaxed mb-8">
              <p>
                Our specialty care services are designed for seniors and adults with unique health needs or chronic conditions that require focused attention. The carers we match provide personalised support tailored to each individual, ensuring comfort, safety, and well-being. From managing complex daily routines to assisting with specialised equipment, our goal is to help clients maintain independence while receiving the care they need.
              </p>
              <p>
                Families can trust our carers to provide compassionate, consistent support that enhances daily life. By coordinating with healthcare providers and creating customised care plans, we ensure each client receives the right level of attention, promoting both physical health and emotional well-being.
              </p>
              <p className="text-ink font-medium">Our services include:</p>
            </div>

            <ul className="space-y-3">
              {['Chronic condition support', 'Care coordination', 'Personalised routines'].map((item) => (
                <li key={item} className="flex items-center gap-3">
                  <span className="shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border border-primary text-primary">
                    <ArrowRight size={13} />
                  </span>
                  <span className="text-ink font-medium">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Right Image */}
          <div className="lg:order-2 order-1">
            <img
              src={specialtyCareImage}
              alt="A carer supporting an older couple at home"
              className="w-full h-[420px] lg:h-[520px] object-cover rounded-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdditionalServicesSection;
